import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, MessageCircle, Heart, Send, Search, Filter,
  AlertTriangle, ThumbsUp, Clock, User, Plus, X
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from '../hooks/use-toast';

const Community = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showNewQuestion, setShowNewQuestion] = useState(false);
  const [showAnswerDialog, setShowAnswerDialog] = useState(null);
  const [newQuestion, setNewQuestion] = useState({ content: '', category: 'general' });
  const [newAnswer, setNewAnswer] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchCategories();
    fetchQuestions();
  }, [isAuthenticated, navigate, selectedCategory, page, authLoading]);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/community/categories');
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/community/questions?category=${selectedCategory}&page=${page}`);
      setQuestions(res.data.questions || []);
      setTotalPages(res.data.pages || 1);
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitQuestion = async () => {
    if (!newQuestion.content || newQuestion.content.length < 10) {
      toast({ title: 'Erreur', description: 'Question trop courte (min. 10 caractères)', variant: 'destructive' });
      return;
    }

    try {
      await api.post('/community/questions', newQuestion);
      toast({ title: 'Question soumise!', description: 'Elle sera visible après modération.' });
      setShowNewQuestion(false);
      setNewQuestion({ content: '', category: 'general' });
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Impossible de soumettre', variant: 'destructive' });
    }
  };

  const handleSubmitAnswer = async (questionId) => {
    if (!newAnswer || newAnswer.length < 5) {
      toast({ title: 'Erreur', description: 'Réponse trop courte', variant: 'destructive' });
      return;
    }

    try {
      await api.post(`/community/questions/${questionId}/answer`, { content: newAnswer });
      toast({ title: 'Réponse ajoutée!' });
      setShowAnswerDialog(null);
      setNewAnswer('');
      fetchQuestions();
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const handleLike = async (questionId) => {
    try {
      const res = await api.post(`/community/questions/${questionId}/like`);
      // Update local state
      setQuestions(prev => prev.map(q => {
        if (q.id === questionId) {
          return { ...q, likes: res.data.liked ? q.likes + 1 : q.likes - 1 };
        }
        return q;
      }));
    } catch (error) {
      console.error('Error liking:', error);
    }
  };

  const handleReport = async (questionId) => {
    try {
      await api.post(`/community/questions/${questionId}/report`, { reason: 'inappropriate' });
      toast({ title: 'Signalé', description: 'Merci pour votre vigilance' });
    } catch (error) {
      console.error('Error reporting:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR');
  };

  const getCategoryIcon = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat?.icon || '❓';
  };

  if (loading && questions.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-white p-2">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold text-white">Communauté</h1>
          <button 
            onClick={() => setShowNewQuestion(true)} 
            className="text-white p-2"
            data-testid="new-question-btn"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Info Banner */}
        <Card className="p-4 bg-purple-500/20 border-purple-400/30">
          <div className="flex items-start gap-3">
            <MessageCircle className="w-5 h-5 text-purple-300 mt-0.5" />
            <div>
              <p className="text-white font-medium text-sm">Espace 100% anonyme</p>
              <p className="text-purple-200 text-xs">
                Pose tes questions librement. Ton identité n'est jamais révélée.
              </p>
            </div>
          </div>
        </Card>

        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => { setSelectedCategory('all'); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
              selectedCategory === 'all' 
                ? 'bg-pink-500 text-white' 
                : 'bg-white/10 text-pink-200'
            }`}
          >
            Tout
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => { setSelectedCategory(cat.id); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                selectedCategory === cat.id 
                  ? 'bg-pink-500 text-white' 
                  : 'bg-white/10 text-pink-200'
              }`}
            >
              {cat.icon} {cat.name}
            </button>
          ))}
        </div>

        {/* Questions List */}
        {questions.length === 0 ? (
          <Card className="p-8 bg-white/10 border-white/20 text-center">
            <MessageCircle className="w-12 h-12 text-pink-300 mx-auto mb-3 opacity-50" />
            <p className="text-white font-medium">Aucune question pour l'instant</p>
            <p className="text-pink-200 text-sm mt-1">Sois la première à poser une question!</p>
            <Button 
              onClick={() => setShowNewQuestion(true)}
              className="mt-4 bg-pink-500 hover:bg-pink-600"
            >
              Poser une question
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {questions.map((question) => (
              <Card key={question.id} className="p-4 bg-white/10 border-white/20">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{getCategoryIcon(question.category)}</span>
                    <span className="text-pink-300 text-xs">Anonyme #{question.anonymousId}</span>
                  </div>
                  <span className="text-pink-200 text-xs flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(question.createdAt)}
                  </span>
                </div>

                <p className="text-white mb-4">{question.content}</p>

                {/* Answers Preview */}
                {question.answers && question.answers.length > 0 && (
                  <div className="border-t border-white/10 pt-3 mt-3 space-y-2">
                    <p className="text-pink-300 text-xs font-medium">
                      {question.answers.length} réponse(s)
                    </p>
                    {question.answers.slice(0, 2).map((answer, idx) => (
                      <div key={idx} className="bg-white/5 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <User className="w-3 h-3 text-pink-300" />
                          <span className="text-pink-300 text-xs">#{answer.anonymousId}</span>
                          {answer.isExpert && (
                            <span className="px-1.5 py-0.5 bg-green-500/20 text-green-300 text-xs rounded">Expert</span>
                          )}
                        </div>
                        <p className="text-pink-100 text-sm">{answer.content}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
                  <div className="flex items-center gap-4">
                    <button 
                      onClick={() => handleLike(question.id)}
                      className="flex items-center gap-1 text-pink-300 hover:text-pink-200 transition-colors"
                    >
                      <ThumbsUp className="w-4 h-4" />
                      <span className="text-sm">{question.likes || 0}</span>
                    </button>
                    <button 
                      onClick={() => setShowAnswerDialog(question)}
                      className="flex items-center gap-1 text-pink-300 hover:text-pink-200 transition-colors"
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span className="text-sm">Répondre</span>
                    </button>
                  </div>
                  <button 
                    onClick={() => handleReport(question.id)}
                    className="text-pink-300/50 hover:text-red-400 transition-colors"
                    title="Signaler"
                  >
                    <AlertTriangle className="w-4 h-4" />
                  </button>
                </div>
              </Card>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 pt-4">
                <Button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white"
                >
                  Précédent
                </Button>
                <span className="text-pink-200 px-4 py-2 text-sm">
                  {page} / {totalPages}
                </span>
                <Button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white"
                >
                  Suivant
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* New Question Dialog */}
      <Dialog open={showNewQuestion} onOpenChange={setShowNewQuestion}>
        <DialogContent className="bg-purple-900 border-purple-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Poser une question anonyme</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-pink-200 text-sm mb-2 block">Catégorie</label>
              <Select 
                value={newQuestion.category} 
                onValueChange={(v) => setNewQuestion({ ...newQuestion, category: v })}
              >
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-purple-800 border-purple-600">
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-pink-200 text-sm mb-2 block">Ta question</label>
              <Textarea
                value={newQuestion.content}
                onChange={(e) => setNewQuestion({ ...newQuestion, content: e.target.value })}
                placeholder="Écris ta question ici... (anonyme)"
                className="bg-white/10 border-white/20 text-white min-h-[120px]"
                maxLength={1000}
              />
              <p className="text-pink-300 text-xs mt-1 text-right">
                {newQuestion.content.length}/1000
              </p>
            </div>
            <Button 
              onClick={handleSubmitQuestion}
              className="w-full bg-pink-500 hover:bg-pink-600"
            >
              <Send className="w-4 h-4 mr-2" />
              Soumettre (anonyme)
            </Button>
            <p className="text-pink-300 text-xs text-center">
              Ta question sera visible après modération
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Answer Dialog */}
      <Dialog open={!!showAnswerDialog} onOpenChange={(open) => !open && setShowAnswerDialog(null)}>
        <DialogContent className="bg-purple-900 border-purple-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Répondre anonymement</DialogTitle>
          </DialogHeader>
          {showAnswerDialog && (
            <div className="space-y-4 py-4">
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-pink-100 text-sm">{showAnswerDialog.content}</p>
              </div>
              <Textarea
                value={newAnswer}
                onChange={(e) => setNewAnswer(e.target.value)}
                placeholder="Ta réponse... (anonyme)"
                className="bg-white/10 border-white/20 text-white min-h-[100px]"
                maxLength={500}
              />
              <Button 
                onClick={() => handleSubmitAnswer(showAnswerDialog.id)}
                className="w-full bg-pink-500 hover:bg-pink-600"
              >
                <Send className="w-4 h-4 mr-2" />
                Répondre
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BottomNav active="community" />
    </div>
  );
};

export default Community;
